import React, { useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import UserAvatar from '../../common/avatar/UserAvatar';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../common/pillTabs.css';
import AllOrder from './AllOrder';
import ResourceAndArticle from './ResourceAndArticle';
import JoinedForum from './JoinedForum';
import AllPost from './AllPost';
import { updateUserApi } from '../../api';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useConfirm } from '../../common/confirmDialog/useConfirm';
import configuration from '../../config/Config';
import StatusChip from '../../common/statusChip/StatusChip';

const Field = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', fontFamily: 'Heebo', wordBreak: 'break-word' }}>
      {children || '—'}
    </Typography>
  </Box>
);

const TABS = ['All Orders', 'Resources & Articles', 'Joined Forums', 'All Posts'];

/* ─── Page ────────────────────────────────────────────────────────── */
const UserInformation = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { data } = useLocation().state || {};

  const [activeTab, setActiveTab] = useState(0);
  const [accStatus, setAccStatus] = useState(data?.account_status);

  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete User', description: 'Are you sure you want to delete this user?', confirmText: 'Delete', destructive: true });
    if (!ok) return;
    try {
      const res = await updateUserApi({ token, id: data._id, account_status: 4 });
      setAccStatus(res?.data?.account_status);
      navigate('/user-management');
      toast.success(res.message);
    } catch (e) { toast.error(e.message); }
  };

  const handleBlock = async () => {
    const ok = await confirm({ title: 'Block User', description: 'Are you sure you want to block this user?', confirmText: 'Block' });
    if (!ok) return;
    try {
      const res = await updateUserApi({ token, id: data._id, account_status: 3 });
      setAccStatus(res?.data?.account_status);
      toast.success(res.message);
    } catch (e) { toast.error(e.message); }
  };

  const handleResume = async () => {
    const ok = await confirm({ title: 'Resume User', description: 'Are you sure you want to resume this user?', confirmText: 'Resume' });
    if (!ok) return;
    try {
      const res = await updateUserApi({ token, id: data._id, account_status: 2 });
      setAccStatus(res?.data?.account_status);
      toast.success(res.message);
    } catch (e) { toast.error(e?.message || e?.error); }
  };

  const addr = data?.address;
  const addressLine = [addr?.address, addr?.city, addr?.state, addr?.country, addr?.zipcode].filter(Boolean).join(', ');

  return (
    <Box sx={{ maxWidth: '1420px' }}>

      {/* Back */}
      <Box
        onClick={() => navigate(-1)}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, cursor: 'pointer', mt: 2, mb: 3, color: '#065F46', '&:hover': { opacity: 0.75 } }}
      >
        <ArrowBackIcon sx={{ fontSize: 20 }} />
        <Typography sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Heebo', color: '#065F46' }}>Back</Typography>
      </Box>

      {/* Profile card */}
      <Box sx={{ bgcolor: '#fff', borderRadius: '24px', boxShadow: '3px 2px 20px 0px rgba(0,0,0,0.04)', p: { xs: 2.5, sm: 4 } }}>

        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo' }}>
            User Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              onClick={handleDelete}
              size="small"
              sx={{ borderRadius: '20px', bgcolor: '#FEE2E2', color: '#991B1B', fontFamily: 'Heebo', fontWeight: 600, fontSize: 13, px: 2.5, textTransform: 'none', '&:hover': { bgcolor: '#fecaca' } }}
            >
              Delete
            </Button>
            <Button
              onClick={accStatus === 3 ? handleResume : handleBlock}
              size="small"
              sx={{ borderRadius: '20px', bgcolor: '#F3F4F6', color: '#362B24', fontFamily: 'Heebo', fontWeight: 600, fontSize: 13, px: 2.5, textTransform: 'none', '&:hover': { bgcolor: '#e5e7eb' } }}
            >
              {accStatus === 3 ? 'Resume' : 'Block'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Profile identity */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 2.5, bgcolor: '#F8F9FA', borderRadius: '18px', mb: 3, flexWrap: 'wrap' }}>
          <UserAvatar
            src={data?.picture ? `${configuration.cdnFront}/${data.picture}` : undefined}
            name={data?.fullname}
            size={72}
            sx={{ flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo', mb: 0.5 }}>
              {data?.fullname || '—'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {data?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 15, color: '#065F46' }} />
                  <Typography sx={{ fontSize: 13, color: '#065F46', fontFamily: 'Heebo', wordBreak: 'break-all' }}>{data.email}</Typography>
                </Box>
              )}
              {data?.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LocalPhoneOutlinedIcon sx={{ fontSize: 15, color: '#065F46' }} />
                  <Typography sx={{ fontSize: 13, color: '#065F46', fontFamily: 'Heebo' }}>{data.phone}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Fields grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)' }, gap: 3, mb: 3 }}>
          <Field label="Status"><StatusChip value={accStatus} /></Field>
          <Field label="Date of Birth">
            {data?.dob && !isNaN(new Date(data.dob)) ? new Date(data.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
          </Field>
          <Field label="Gender">{data?.gender || '—'}</Field>
          <Field label="City">{addr?.city || '—'}</Field>
          <Field label="State">{addr?.state || '—'}</Field>
          <Field label="Country">{addr?.country || '—'}</Field>
          <Field label="Zip Code">{addr?.zipcode || '—'}</Field>
        </Box>

        {/* Address row */}
        {addressLine && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 2, bgcolor: '#F8F9FA', borderRadius: '14px' }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 17, color: '#065F46', mt: 0.15, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.3 }}>
                Address
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.6 }}>
                {addressLine}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ mt: 3 }}>
        <div className="pill-box" style={{ width: '580px', maxWidth: '100%' }}>
          <div
            className="pill-slider"
            style={{
              left: `calc(5px + ${activeTab} * (100% - 10px) / ${TABS.length})`,
              width: `calc((100% - 10px) / ${TABS.length})`,
            }}
          />
          {TABS.map((label, i) => (
            <button key={i} className={`pill-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
              {label}
            </button>
          ))}
        </div>
      </Box>

      {activeTab === 0 && <AllOrder />}
      {activeTab === 1 && <ResourceAndArticle />}
      {activeTab === 2 && <JoinedForum />}
      {activeTab === 3 && <AllPost />}
    </Box>
  );
};

export default UserInformation;
